import { Item, Rune, TooltipContainer } from "@outplayed/tooltips";
import React, { useEffect } from "react";
import classNames from "classnames";
import { compile } from "path-to-regexp";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { Link, useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import { MediaQuery } from "@outplayed/responsive";
import { ReactComponent as Warning } from "svg/warning.svg";
import { ReactComponent as FeedbackIcon } from "svg/feedback-icon.svg";
import { getChampionBuildUrl, getSpecialGameModeChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { SPECIAL_CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import AppDownloadButton from "components/Buttons/AppDownloadButton";

const MythicItem = (props) => {
  const { itemId } = props;
  const { useRiotItems, isMythicItem } = getRiotAssetsContext();
  const { data } = useRiotItems({ itemId });

  return props.children(isMythicItem((data || {})[itemId]));
};
const BuildTabs = (props) => {
  const {
    page,
    version,
    tabData,
    defaultBuild,
    setDefaultBuild,
    isAP,
    isTank,
    isAD,
    isCrit,
    isLethality,
    isOnHit,
    isRed,
    isBlue,
    championId,
  } = props;

  const { getNormalizedChampionName } = getRiotAssetsContext();
  const championName = getNormalizedChampionName(championId).toLowerCase();
  const location = useLocation();
  const history = useHistory();
  const params = useParams();
  const { path } = useRouteMatch();
  const isSpecialGameMode = Object.values(SPECIAL_CHAMPION_PAGES).includes(page);
  const isKayn = championId == 141;
  const redKaynId = "100141";
  const blueKaynId = "200141";

  const multiURL = (multi) => {
    const toPath = compile(path);
    return toPath({ ...params, multi }) + location.search;
  };

  useEffect(() => {
    if (championId == 141) {
      setDefaultBuild(params.multi == "red" ? 100141 : 200141);
    }
  }, [params]);

  const tooltipInfo = (
    <div className="multibuilds-tooltip">
      <p>
        Multibuilds are new and still in BETA. We appreciate any and all feedback (
        <span className="image-wrapper">
          <FeedbackIcon style={{ display: "inline-block", height: "10px", width: "10px" }} />
        </span>
        ) as we continue to improve it.
      </p>
    </div>
  );

  function capitalizeMulti(multi) {
    switch (multi) {
      case "ap":
      case "ad":
        return multi.toUpperCase();
      case "tank":
      case "crit":
      case "lethality":
        return multi.substring(0, 1).toUpperCase() + multi.substring(1);
      case "onhit":
        return "On Hit";
      case "red":
        return "Red";
      case "blue":
        return "Blue";
      default:
        return "Recommended";
    }
  }

  function convertObjectToArray(obj) {
    const arr = Object.entries(obj).map(([key, value]) => [...value, key]);
    arr.sort((a, b) => b[2] - a[2]);
    return arr;
  }

  const eventFire = (multi, index) => {
    const build = capitalizeMulti(multi);
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "multibuild-tab-select",
        category: "filter",
        filterType: "multibuild",
        filterValue: build + "-" + championName + "-tab-" + (index + 1),
      });
    }
  };

  const tabDataArray = convertObjectToArray(tabData);

  useEffect(() => {
    let someActive = false;
    if (isKayn) {
      someActive = [tabData?.[parseInt(redKaynId)], tabData?.[parseInt(blueKaynId)]].some((val, idx) => {
        const selector = idx ? "blue" : "red";
        return params?.multi === selector || (selector === "red" && !params?.multi);
      });
    } else {
      someActive = tabDataArray.some((data, idx) => {
        const multiData = data[3];
        return params?.multi === multiData || (multiData === "default" && !params?.multi);
      });
    }

    if (!someActive) {
      const gameMode = (params.champhyphened || "").split(/-(.*)/s)?.[1];
      history.replace(
        isSpecialGameMode && gameMode
          ? {
              pathname: getSpecialGameModeChampionBuildUrl(championName, gameMode, { multi: isKayn ? "red" : undefined }),
              search: location.search,
            }
          : {
              pathname: getChampionBuildUrl(championName, { role: params?.role }),
              search: location.search,
            },
      );
    }
  }, [location.search, isSpecialGameMode, isKayn, params?.multi]);

  return championId == 141 ? (
    <div className="build-tabs">
      <div onClick={() => eventFire("Red Kayn", "1")}>
        <Link className={classNames("build-tab", { active: isRed })} to={multiURL("red")}>
          <div className="build-name">Red Kayn</div>
          <div className="rune-image-container">
            <Rune patch={version} runeId={tabData[100141] && tabData[100141][0]} ssr />
          </div>
          {tabData[100141] && tabData[100141][1] != 0 && (
            <MythicItem itemId={tabData[100141] && tabData[100141][1]}>
              {(isMythic) => {
                return (
                  <div className={classNames("item-image-container", { "is-mythic": isMythic })}>
                    <Item itemId={tabData[100141] && tabData[100141][1]} ssr />
                  </div>
                );
              }}
            </MythicItem>
          )}
        </Link>
      </div>
      <div onClick={() => eventFire("Blue Kayn", "2")}>
        <Link className={classNames("build-tab", { active: isBlue || params.multi == undefined })} to={multiURL("blue")}>
          <div className="build-name">Blue Kayn</div>
          <div className="rune-image-container">
            <Rune patch={version} runeId={tabData[200141] && tabData[200141][0]} ssr />
          </div>
          {tabData[200141] && tabData[200141][1] != 0 && (
            <MythicItem itemId={tabData[200141] && tabData[200141][1]}>
              {(isMythic) => {
                return (
                  <div className={classNames("item-image-container", { "is-mythic": isMythic })}>
                    <Item itemId={tabData[200141] && tabData[200141][1]} ssr />
                  </div>
                );
              }}
            </MythicItem>
          )}
        </Link>
      </div>
    </div>
  ) : (
    <div className="build-tabs">
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        {tabDataArray.map((data, index) => (
          <div key={index} onClick={() => eventFire(data[data.length - 1], index)}>
            <Link
              className={classNames("build-tab multi-build", {
                active:
                  params.multi === data[data.length - 1] || ("default" === data[data.length - 1] && params.multi == undefined),
                rec: data[data.length - 1] === "default" ? true : false,
              })}
              to={multiURL(data[data.length - 1] === "default" ? undefined : data[data.length - 1])}
            >
              <div className="build-name">{capitalizeMulti(data[data.length - 1])}</div>
              <div className="rune-image-container">
                <Rune patch={version} runeId={data && data[0]} ssr />
              </div>
              {data && data[1] != 0 && (
                <MythicItem itemId={data && data[1]}>
                  {(isMythic) => {
                    return (
                      <div className={classNames("item-image-container", { "is-mythic": isMythic })}>
                        <Item itemId={data && data[1]} ssr />
                      </div>
                    );
                  }}
                </MythicItem>
              )}
            </Link>
          </div>
        ))}
      </MediaQuery>
      <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
        <AppDownloadButton className="auto-import-button" url={"/app?utm_source=uggbuild"} label="Auto-Import" icon={"cloud"} />
      </MediaQuery>
      <TooltipContainer tooltipInfo={tooltipInfo} customStyle={{ maxWidth: "400px" }} tooltipHoverable>
        <div className="tooltip">
          <Warning />
        </div>
      </TooltipContainer>
    </div>
  );
};

export default BuildTabs;
